import React, { Component } from "react"
import Highcharts from "highcharts/highstock"
import Exporting from "highcharts/modules/exporting"
import PropTypes from "prop-types"
import { translate } from "react-i18next"
import isEqual from "lodash/isEqual"
import { CHARTS } from "../../../constants"

class LineChart extends Component {
  componentDidMount = () => {
    this.createChart()
  }

  shouldComponentUpdate = (nextProps, nextState) => {
    return !isEqual(this.props.data, nextProps.data)
  }

  componentDidUpdate = () => {
    this.createChart()
  }

  componentWillUnmount = () => {
    this.chart.destroy()
  }

  createChart = () => {
    const {
      yAxisLabel,
      yTickInterval,
      yMinValue,
      yMaxValue,
      xAxisLabel,
      xTickInterval,
      chartPointStart,
      data,
      categories,
      t,
      height,
      xAxisFormatter,
      tooltipFormatter,
      titleKey,
      subtitleKey
    } = this.props

    // Get the element into which to insert the chart
    const chartContainer = this.chartContainerRef

    Exporting(Highcharts)

    // Create the chart
    this.chart = new Highcharts.Chart({
      chart: {
        renderTo: chartContainer,
        height,
        type: "line",
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        animation: CHARTS.animationEnabled,
        scrollablePlotArea: {
          minWidth: 600,
          scrollPositionX: 1
        }
      },
      lang: {
        contextButtonTitle: t("Common.ChartDownloadOptions")
      },
      yAxis: {
        title: {
          text: t(yAxisLabel)
        },
        allowDecimals: false,
        gridLineDashStyle: "ShortDot",
        tickInterval: yTickInterval,
        min: yMinValue,
        max: yMaxValue
      },
      xAxis: {
        title: {
          text: t(xAxisLabel)
        },
        tickInterval: xTickInterval,
        labels: {
          formatter() {
            if (xAxisFormatter) {
              return xAxisFormatter(this.value)
            }
            return this.value
          }
        }
      },
      credits: {
        enabled: false
      },
      colors: CHARTS.getLineColours(),
      title: {
        text: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        y: -30
      },
      subtitle: {
        text: subtitleKey != null && subtitleKey.length > 0 ? t(subtitleKey) : null,
        y: -15
      },
      plotOptions: {
        series: {
          label: {
            connectorAllowed: false,
            cursor: "pointer",
            dataLabels: {
              enabled: false
            }
          },
          pointStart: chartPointStart,
          animation: false
        }
      },
      tooltip: {
        formatter() {
          if (tooltipFormatter) {
            return tooltipFormatter(this)
          }

          return `<b>${this.x}:</b> ${this.y}`
        }
      },
      exporting: {
        enabled: CHARTS.exportEnabled,
        buttons: {
          contextButton: {
            menuItems: CHARTS.exportButtons
          }
        },
        filename: titleKey != null && titleKey.length > 0 ? t(titleKey) : null,
        chartOptions: CHARTS.exportChartOptions
      }
    })

    // Set the data in the chart
    // If there is only one data set in the returned data then we want to hide the series.
    data.map(x => {
      return this.chart.addSeries({
        name: x.name,
        data: x.data,
        showInLegend: !(data.length === 1)
      })
    })

    // If 'Categories' has been set then we need to add them to the chart
    if (categories != null) {
      this.chart.xAxis[0].setCategories(categories, true)
    }
  }

  render() {
    // Set the widget width: 1, 2 or 3 thirds
    let columnWidth = ""
    switch (this.props.columns) {
      case 1:
        columnWidth = "4"
        break
      case 2:
        columnWidth = "8"
        break
      default:
        columnWidth = "12"
    }
    return (
      <div
        className={`chart-container col-lg-${columnWidth} col-md-12`}
        style={{
          display: this.props.displayInline ? "inline-block" : ""
        }}
        ref={c => {
          this.chartContainerRef = c
        }}
      />
    )
  }
}

LineChart.propTypes = {
  yAxisLabel: PropTypes.string.isRequired,
  yMinValue: PropTypes.number,
  yMaxValue: PropTypes.number,
  xAxisLabel: PropTypes.string.isRequired,
  xAxisFormatter: PropTypes.func,
  tooltipFormatter: PropTypes.func,
  xTickInterval: PropTypes.number,
  // Sets where the data starts, e.g. if you set to "1" it would skip your first category
  chartPointStart: PropTypes.number,
  yTickInterval: PropTypes.number,
  columns: PropTypes.number,
  displayInline: PropTypes.bool,
  t: PropTypes.func.isRequired,
  categories: PropTypes.array,
  data: PropTypes.array,
  height: PropTypes.number,
  titleKey: PropTypes.string,
  subtitleKey: PropTypes.string
}

LineChart.defaultProps = {
  chartPointStart: 0,
  yMinValue: undefined,
  yMaxValue: undefined,
  yTickInterval: null,
  xTickInterval: null,
  displayInline: false,
  columns: 3,
  height: CHARTS.standardHeight,
  tooltipFormatter: null,
  xAxisFormatter: null,
  titleKey: "",
  subtitleKey: ""
}

export default translate()(LineChart)



// WEBPACK FOOTER //
// ./src/components/charts/generic/LineChart.jsx